/**
 * Operational Costs Tab Component
 * Manages non-Amazon operational costs (DDP, warehouse, customs, shipping per desi, etc.)
 * Amazon expenses come from transaction data, not from here.
 */

import React, { useState, useEffect } from 'react';
import {
  Loader2,
  Save,
  RefreshCw,
  Truck,
  Building2,
  Package,
  Percent,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Globe,
  Plus,
  Trash2,
  Tag
} from 'lucide-react';
import { useLanguage } from '../../i18n';
import {
  apiGetAllProfitConfigs,
  apiSaveProfitConfig,
  apiGetProductCategories,
  MarketplaceProfitConfig,
  FBAConfig,
  FBMConfig,
  GSTConfig
} from '../../services/api';
import { logger } from '../../utils/logger';

// Country flags mapping
const FLAGS: Record<string, string> = {
  US: '🇺🇸',
  UK: '🇬🇧',
  DE: '🇩🇪',
  FR: '🇫🇷',
  IT: '🇮🇹',
  ES: '🇪🇸',
  CA: '🇨🇦',
  AU: '🇦🇺',
  AE: '🇦🇪',
  SA: '🇸🇦',
  SG: '🇸🇬',
  JP: '🇯🇵',
  MX: '🇲🇽',
  BR: '🇧🇷',
  IN: '🇮🇳',
};

// Currency symbols
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  AED: 'AED',
  SAR: 'SAR',
  SGD: 'S$',
  JPY: '¥',
  MXN: 'MX$',
  BRL: 'R$',
  INR: '₹',
  TRY: '₺',
};

interface ProfitSettingsTabProps {
  onSave?: () => void;
}

const ProfitSettingsTab: React.FC<ProfitSettingsTabProps> = ({ onSave }) => {
  const { language } = useLanguage();
  const [configs, setConfigs] = useState<MarketplaceProfitConfig[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedMarketplace, setExpandedMarketplace] = useState<string | null>(null);
  const [editedConfigs, setEditedConfigs] = useState<Record<string, MarketplaceProfitConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfigs();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await apiGetProductCategories();
      setCategories(data);
    } catch (err) {
      logger.error('Failed to load categories:', err);
    }
  };

  const loadConfigs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiGetAllProfitConfigs();
      setConfigs(data);
      // Initialize edited configs
      const edited: Record<string, MarketplaceProfitConfig> = {};
      data.forEach(c => {
        edited[c.marketplace_code] = { ...c };
      });
      setEditedConfigs(edited);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const updateConfig = (code: string, updates: Partial<MarketplaceProfitConfig>) => {
    setEditedConfigs(prev => ({
      ...prev,
      [code]: { ...prev[code], ...updates }
    }));
    setHasChanges(true);
  };

  const updateFBAConfig = (code: string, updates: Partial<FBAConfig>) => {
    const config = editedConfigs[code];
    if (!config) return;

    const currentFBA = typeof config.fba_config === 'string'
      ? JSON.parse(config.fba_config)
      : config.fba_config || {};

    updateConfig(code, {
      fba_config: { ...currentFBA, ...updates }
    });
  };

  const updateFBMFromTR = (code: string, field: string, value: number) => {
    const config = editedConfigs[code];
    if (!config) return;

    const currentFBM = typeof config.fbm_config === 'string'
      ? JSON.parse(config.fbm_config)
      : config.fbm_config || {};

    const currentFromTR = currentFBM.fromTR || {};

    updateConfig(code, {
      fbm_config: {
        ...currentFBM,
        fromTR: { ...currentFromTR, [field]: value }
      }
    });
  };

  const addCategoryDuty = (code: string) => {
    const config = editedConfigs[code];
    if (!config) return;

    const currentFBM = typeof config.fbm_config === 'string'
      ? JSON.parse(config.fbm_config)
      : config.fbm_config || {};

    const currentFromTR = currentFBM.fromTR || {};
    const categoryDuties = currentFromTR.categoryDuties || [];

    updateConfig(code, {
      fbm_config: {
        ...currentFBM,
        fromTR: {
          ...currentFromTR,
          categoryDuties: [...categoryDuties, { category: '', dutyPercent: 0 }]
        }
      }
    });
  };

  const updateCategoryDuty = (code: string, index: number, field: 'category' | 'dutyPercent', value: string | number) => {
    const config = editedConfigs[code];
    if (!config) return;

    const currentFBM = typeof config.fbm_config === 'string'
      ? JSON.parse(config.fbm_config)
      : config.fbm_config || {};

    const currentFromTR = currentFBM.fromTR || {};
    const categoryDuties = [...(currentFromTR.categoryDuties || [])];

    categoryDuties[index] = {
      ...categoryDuties[index],
      [field]: field === 'dutyPercent' ? parseFloat(value as string) || 0 : value
    };

    updateConfig(code, {
      fbm_config: {
        ...currentFBM,
        fromTR: {
          ...currentFromTR,
          categoryDuties
        }
      }
    });
  };

  const removeCategoryDuty = (code: string, index: number) => {
    const config = editedConfigs[code];
    if (!config) return;

    const currentFBM = typeof config.fbm_config === 'string'
      ? JSON.parse(config.fbm_config)
      : config.fbm_config || {};

    const currentFromTR = currentFBM.fromTR || {};
    const categoryDuties = [...(currentFromTR.categoryDuties || [])];
    categoryDuties.splice(index, 1);

    updateConfig(code, {
      fbm_config: {
        ...currentFBM,
        fromTR: {
          ...currentFromTR,
          categoryDuties
        }
      }
    });
  };

  const updateGSTConfig = (code: string, updates: Partial<GSTConfig> | null) => {
    const config = editedConfigs[code];
    if (!config) return;

    if (updates === null) {
      updateConfig(code, { gst_config: null });
    } else {
      const currentGST = typeof config.gst_config === 'string'
        ? JSON.parse(config.gst_config)
        : config.gst_config || {};

      updateConfig(code, {
        gst_config: { ...currentGST, ...updates }
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Save all edited configs
      for (const code of Object.keys(editedConfigs)) {
        const config = editedConfigs[code];
        await apiSaveProfitConfig({
          marketplace_code: config.marketplace_code,
          marketplace_name: config.marketplace_name,
          currency: config.currency,
          fba_config: config.fba_config,
          fbm_config: config.fbm_config,
          gst_config: config.gst_config,
          is_active: config.is_active
        });
      }

      setSuccessMessage(language === 'tr' ? 'Ayarlar kaydedildi' : 'Settings saved');
      setHasChanges(false);
      onSave?.();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const getFBAConfig = (config: MarketplaceProfitConfig): FBAConfig => {
    if (typeof config.fba_config === 'string') {
      return JSON.parse(config.fba_config);
    }
    return config.fba_config || { shippingPerDesi: 1, warehousePercent: 0 };
  };

  const getFBMConfig = (config: MarketplaceProfitConfig): FBMConfig => {
    if (typeof config.fbm_config === 'string') {
      return JSON.parse(config.fbm_config);
    }
    return config.fbm_config || { shippingMode: 'TR', fromTR: { customsDutyPercent: 8.5, ddpFee: 2.5 } };
  };

  const getGSTConfig = (config: MarketplaceProfitConfig): GSTConfig | null => {
    if (!config.gst_config) return null;
    if (typeof config.gst_config === 'string') {
      return JSON.parse(config.gst_config);
    }
    return config.gst_config;
  };

  const toggleExpand = (code: string) => {
    setExpandedMarketplace(expandedMarketplace === code ? null : code);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-slate-700">
            {language === 'tr' ? 'Operasyonel Maliyetler' : 'Operational Costs'}
          </h4>
          <p className="text-xs text-slate-500 mt-1">
            {language === 'tr'
              ? 'Marketplace dışı maliyetler: DDP, depo, gümrük ve kargo. Amazon masrafları transaction verilerinden gelir.'
              : 'Non-Marketplace costs: DDP, warehouse, customs, shipping. Amazon expenses come from transaction data.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadConfigs}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {language === 'tr' ? 'Kaydet' : 'Save'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 p-4 bg-green-50 text-green-700 rounded-lg">
          <CheckCircle className="w-5 h-5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Marketplace List */}
      <div className="space-y-3">
        {Object.values(editedConfigs).map((config) => {
          const isExpanded = expandedMarketplace === config.marketplace_code;
          const fbaConfig = getFBAConfig(config);
          const fbmConfig = getFBMConfig(config);
          const gstConfig = getGSTConfig(config);
          const currencySymbol = CURRENCY_SYMBOLS[config.currency] || config.currency;
          const flag = FLAGS[config.marketplace_code] || '🌍';

          return (
            <div key={config.marketplace_code} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {/* Header */}
              <button
                onClick={() => toggleExpand(config.marketplace_code)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{flag}</span>
                  <div className="text-left">
                    <div className="font-medium text-slate-800">{config.marketplace_name}</div>
                    <div className="text-xs text-slate-500">{config.marketplace_code} • {config.currency}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Quick stats - all monetary values in USD */}
                  <div className="hidden md:flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      ${fbaConfig.shippingPerDesi}/desi
                    </span>
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {fbaConfig.warehousePercent}%
                    </span>
                    <span className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {fbmConfig.fromTR?.customsDutyPercent || 0}%
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                    {/* FBA Settings */}
                    <div className="space-y-4">
                      <h5 className="flex items-center gap-2 font-medium text-slate-700">
                        <Package className="w-4 h-4 text-orange-500" />
                        FBA {language === 'tr' ? 'Ayarları' : 'Settings'}
                      </h5>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">
                            {language === 'tr' ? 'Depoya Gönderim ($/desi)' : 'Shipping to Warehouse ($/desi)'}
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={fbaConfig.shippingPerDesi || ''}
                              onChange={(e) => updateFBAConfig(config.marketplace_code, {
                                shippingPerDesi: parseFloat(e.target.value) || 0
                              })}
                              className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="1.00"
                            />
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {language === 'tr' ? 'Gemi bedeli - desi başı USD' : 'Sea freight per desi in USD'}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs text-slate-600 mb-1">
                            {language === 'tr' ? 'Depo-İdare Gideri (%)' : 'Warehouse/Admin Fee (%)'}
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              step="0.1"
                              value={fbaConfig.warehousePercent || ''}
                              onChange={(e) => updateFBAConfig(config.marketplace_code, {
                                warehousePercent: parseFloat(e.target.value) || 0
                              })}
                              className="w-full pr-8 pl-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="0"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* FBM from TR Settings */}
                    <div className="space-y-4">
                      <h5 className="flex items-center gap-2 font-medium text-slate-700">
                        <Truck className="w-4 h-4 text-blue-500" />
                        FBM (TR{language === 'tr' ? "'den" : ' to'} {config.marketplace_code}) {language === 'tr' ? 'Ayarları' : 'Settings'}
                      </h5>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">
                            {language === 'tr' ? 'Gümrük Vergisi (%)' : 'Customs Duty (%)'}
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              step="0.1"
                              value={fbmConfig.fromTR?.customsDutyPercent || ''}
                              onChange={(e) => updateFBMFromTR(config.marketplace_code, 'customsDutyPercent', parseFloat(e.target.value) || 0)}
                              className="w-full pr-8 pl-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="8.5"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {language === 'tr' ? 'Varsayılan gümrük vergisi oranı' : 'Default customs duty rate'}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs text-slate-600 mb-1">
                            {language === 'tr' ? 'DDP Ücreti ($)' : 'DDP Fee ($)'}
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={fbmConfig.fromTR?.ddpFee || ''}
                              onChange={(e) => updateFBMFromTR(config.marketplace_code, 'ddpFee', parseFloat(e.target.value) || 0)}
                              className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="2.50"
                            />
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {language === 'tr' ? 'Sabit DDP işlem ücreti' : 'Fixed DDP processing fee'}
                          </p>
                        </div>

                        {/* Category-based Customs Duties */}
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <div className="flex items-center justify-between mb-3">
                            <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                              <Tag className="w-3 h-3" />
                              {language === 'tr' ? 'Kategori Bazlı Gümrük Vergileri' : 'Category-based Customs Duties'}
                            </label>
                            <button
                              type="button"
                              onClick={() => addCategoryDuty(config.marketplace_code)}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded"
                            >
                              <Plus className="w-3 h-3" />
                              {language === 'tr' ? 'Ekle' : 'Add'}
                            </button>
                          </div>

                          {fbmConfig.fromTR?.categoryDuties && fbmConfig.fromTR.categoryDuties.length > 0 ? (
                            <div className="space-y-2">
                              {fbmConfig.fromTR.categoryDuties.map((cd: { category: string; dutyPercent: number }, idx: number) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <select
                                    value={cd.category}
                                    onChange={(e) => updateCategoryDuty(config.marketplace_code, idx, 'category', e.target.value)}
                                    className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                  >
                                    <option value="">{language === 'tr' ? 'Kategori seçin...' : 'Select category...'}</option>
                                    {categories.map(cat => (
                                      <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                  </select>
                                  <div className="relative w-24">
                                    <input
                                      type="number"
                                      step="0.1"
                                      value={cd.dutyPercent || ''}
                                      onChange={(e) => updateCategoryDuty(config.marketplace_code, idx, 'dutyPercent', e.target.value)}
                                      placeholder="0"
                                      className="w-full pr-6 pl-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeCategoryDuty(config.marketplace_code, idx)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400 italic">
                              {language === 'tr'
                                ? 'Kategori bazlı vergi tanımlı değil. Varsayılan gümrük vergisi kullanılacak.'
                                : 'No category duties defined. Default customs duty will be used.'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* GST Settings (for AU, AE, SA, SG) */}
                    {['AU', 'AE', 'SA', 'SG', 'IN'].includes(config.marketplace_code) && (
                      <div className="space-y-4 md:col-span-2">
                        <h5 className="flex items-center gap-2 font-medium text-slate-700">
                          <Percent className="w-4 h-4 text-green-500" />
                          GST/VAT {language === 'tr' ? 'Ayarları' : 'Settings'}
                        </h5>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">
                              GST/VAT (%)
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.1"
                                value={gstConfig?.gstPercent || ''}
                                onChange={(e) => updateGSTConfig(config.marketplace_code, {
                                  gstPercent: parseFloat(e.target.value) || 0
                                })}
                                className="w-full pr-8 pl-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="10"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">
                              {language === 'tr' ? 'İthalat Eşiği' : 'Import Threshold'} ({currencySymbol})
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{currencySymbol}</span>
                              <input
                                type="number"
                                step="1"
                                value={gstConfig?.importThreshold || ''}
                                onChange={(e) => updateGSTConfig(config.marketplace_code, {
                                  importThreshold: parseFloat(e.target.value) || 0
                                })}
                                className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="1000"
                              />
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              {language === 'tr' ? 'Bu değerin altındaki siparişlere GST uygulanmaz' : 'Orders below this value are GST exempt'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">
                              {language === 'tr' ? 'Fiyata Dahil' : 'Included in Price'}
                            </label>
                            <button
                              type="button"
                              onClick={() => updateGSTConfig(config.marketplace_code, {
                                includedInPrice: !gstConfig?.includedInPrice
                              })}
                              className={`w-full py-2 px-3 rounded-lg border transition-colors ${
                                gstConfig?.includedInPrice
                                  ? 'bg-green-100 border-green-300 text-green-700'
                                  : 'bg-red-50 border-red-200 text-red-600'
                              }`}
                            >
                              {gstConfig?.includedInPrice
                                ? (language === 'tr' ? 'Evet' : 'Yes')
                                : (language === 'tr' ? 'Hayır' : 'No')
                              }
                            </button>
                            <p className="text-xs text-slate-400 mt-1">
                              {language === 'tr' ? 'VAT satış fiyatına dahil mi?' : 'Is VAT included in sale price?'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {configs.length === 0 && !isLoading && (
        <div className="text-center py-12 text-slate-500">
          <Globe className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>{language === 'tr' ? 'Henüz ülke ayarı bulunmuyor' : 'No country settings found'}</p>
        </div>
      )}
    </div>
  );
};

export default ProfitSettingsTab;
